/**
 * Device-local storage of booking access tokens (the client has no account).
 * Only the token and display hints are kept; everything else is fetched by token.
 * All access is guarded: private mode or blocked storage must not break the app.
 */
export interface SavedBooking {
  id: string;
  token: string;
  startsAt: string;
  serviceName: string;
  savedAt: string;
}

const key = (slug: string) => `bookings:v1:${slug}`;

function read<T>(k: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(k);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}
function write(k: string, v: unknown) {
  try {
    localStorage.setItem(k, JSON.stringify(v));
  } catch {
    /* storage unavailable: the booking link still works for this session */
  }
}

export function savedBookings(slug: string): SavedBooking[] {
  return read<SavedBooking[]>(key(slug), []).filter((b) => typeof b.token === 'string');
}

export function saveBooking(slug: string, b: Omit<SavedBooking, 'savedAt'>) {
  const list = savedBookings(slug).filter((x) => x.id !== b.id);
  list.unshift({ ...b, savedAt: new Date().toISOString() });
  write(key(slug), list.slice(0, 20));
}

export function updateSavedBooking(slug: string, id: string, patch: Partial<SavedBooking>) {
  write(key(slug), savedBookings(slug).map((b) => (b.id === id ? { ...b, ...patch } : b)));
}

export function forgetBooking(slug: string, id: string) {
  write(key(slug), savedBookings(slug).filter((b) => b.id !== id));
}

/** In-progress booking attempt: the same idempotency key survives a reload/retry. */
export function pendingKey(slug: string, fingerprint: string): string {
  const k = `pending-booking:${slug}`;
  try {
    const cur = JSON.parse(sessionStorage.getItem(k) ?? 'null') as { fp: string; key: string } | null;
    if (cur?.fp === fingerprint) return cur.key;
    const fresh = { fp: fingerprint, key: crypto.randomUUID() };
    sessionStorage.setItem(k, JSON.stringify(fresh));
    return fresh.key;
  } catch {
    return crypto.randomUUID();
  }
}
export function clearPending(slug: string) {
  try {
    sessionStorage.removeItem(`pending-booking:${slug}`);
  } catch {
    /* ignore */
  }
}
